import React, { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import {
  Campaign as CampaignIcon,
  EmojiEvents as ContestIcon,
  Language as GlobalIcon,
  Layers as TierIcon,
  SmartDisplay as VideoIcon,
  AutoAwesome as FormatIcon,
  School as ContentIcon,
  Sports as SportsIcon,
} from '@mui/icons-material';
import SponsorPublicShell from '@components/sponsor/SponsorPublicShell';
import ExpressInterestForm from '@components/sponsor/ExpressInterestForm';
import {
  SPONSOR_INTEREST_CATEGORIES,
  SPONSORSHIP_TIERS,
  type FeaturedSponsor,
} from '@utils/sponsor';

const typeIcons: Record<string, React.ReactNode> = {
  contest: <ContestIcon />,
  training_video: <VideoIcon />,
  content_series: <ContentIcon />,
  tournament: <SportsIcon />,
  new_format: <FormatIcon />,
};

const SponsorLandingPage: React.FC = () => {
  const [featured, setFeatured] = useState<FeaturedSponsor[]>([]);

  useEffect(() => {
    void (async () => {
      try {
        const res = await fetch('/api/sponsors/featured');
        const data = await res.json();
        if (data.success) setFeatured(data.data || []);
      } catch {
        // ignore
      }
    })();
  }, []);

  const hero = (
    <Box
      sx={{
        position: 'relative',
        overflow: 'hidden',
        py: { xs: 6, md: 9 },
        background: 'radial-gradient(ellipse 80% 60% at 50% -10%, rgba(99,102,241,0.45) 0%, transparent 55%), linear-gradient(180deg, #0b1220 0%, #111827 100%)',
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          backgroundImage: 'linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)',
          backgroundSize: '48px 48px',
          opacity: 0.5,
        }}
      />
      <Container maxWidth="lg" sx={{ position: 'relative' }}>
        <Grid container spacing={4} alignItems="center">
          <Grid item xs={12} md={7}>
            <Chip
              icon={<GlobalIcon />}
              label="Global platform · India-first rollout"
              sx={{ mb: 2, bgcolor: 'rgba(255,255,255,0.1)', color: '#e2e8f0', fontWeight: 800, border: '1px solid rgba(255,255,255,0.15)' }}
            />
            <Typography
              variant="h2"
              sx={{
                fontWeight: 900,
                color: '#fff',
                lineHeight: 1.1,
                fontSize: { xs: '2rem', md: '3rem' },
                mb: 2,
              }}
            >
              Sponsor contests, training videos &{' '}
              <Box component="span" sx={{ background: 'linear-gradient(90deg, #fbbf24, #f97316)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                learner content
              </Box>
            </Typography>
            <Typography variant="h6" sx={{ color: '#94a3b8', fontWeight: 500, mb: 3, maxWidth: 560 }}>
              Reach players, coaches, and academies through EncycGames contests and skill journeys. Multi-tier packages tailored to your budget and category competition.
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
              <Button
                component={RouterLink}
                to="/sponsors/opportunities"
                variant="contained"
                size="large"
                sx={{
                  textTransform: 'none',
                  fontWeight: 900,
                  background: 'linear-gradient(135deg, #f59e0b 0%, #ea580c 100%)',
                  boxShadow: '0 12px 32px rgba(245, 158, 11, 0.4)',
                }}
              >
                View open opportunities
              </Button>
              <Button
                component={RouterLink}
                to="/sponsors#express-interest"
                variant="outlined"
                size="large"
                sx={{
                  textTransform: 'none',
                  fontWeight: 800,
                  color: '#e2e8f0',
                  borderColor: 'rgba(255,255,255,0.35)',
                }}
              >
                Express interest
              </Button>
              <Button
                component={RouterLink}
                to="/register/sponsor"
                variant="text"
                size="large"
                sx={{ textTransform: 'none', fontWeight: 800, color: '#a5b4fc' }}
              >
                Create sponsor account
              </Button>
            </Stack>
          </Grid>
          <Grid item xs={12} md={5}>
            <Box
              sx={{
                p: 3,
                borderRadius: 4,
                background: 'linear-gradient(145deg, rgba(255,255,255,0.12) 0%, rgba(255,255,255,0.04) 100%)',
                border: '1px solid rgba(255,255,255,0.12)',
                backdropFilter: 'blur(8px)',
              }}
            >
              <Stack spacing={2}>
                {SPONSORSHIP_TIERS.slice(0, 4).map((tier) => (
                  <Box
                    key={tier.id}
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      border: '1px solid rgba(255,255,255,0.1)',
                      bgcolor: 'rgba(0,0,0,0.2)',
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ color: '#fbbf24', fontWeight: 900 }}>{tier.label}</Typography>
                    <Typography variant="caption" sx={{ color: '#cbd5e1' }}>{tier.description}</Typography>
                  </Box>
                ))}
                <Typography variant="caption" sx={{ color: '#64748b', fontStyle: 'italic' }}>
                  Final tier assigned based on budget, category demand, and inventory — contact us for a custom package.
                </Typography>
              </Stack>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );

  return (
    <SponsorPublicShell hero={hero}>
      {featured.length > 0 && (
        <Box sx={{ mb: 5 }}>
          <Typography variant="overline" sx={{ color: '#64748b', fontWeight: 800, letterSpacing: 2 }}>
            Partnership network
          </Typography>
          <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap sx={{ mt: 1.5 }}>
            {featured.map((s) => (
              <Chip
                key={s.id}
                avatar={
                  s.logoUrl ? (
                    <Box component="img" src={s.logoUrl} alt="" sx={{ width: 24, height: 24, borderRadius: '50%', objectFit: 'contain' }} />
                  ) : undefined
                }
                label={s.name}
                sx={{ bgcolor: 'rgba(255,255,255,0.06)', color: '#e2e8f0', fontWeight: 700, border: '1px solid rgba(255,255,255,0.1)' }}
              />
            ))}
          </Stack>
        </Box>
      )}

      <Typography variant="h4" sx={{ fontWeight: 900, color: '#f8fafc', mb: 3 }}>
        What you can sponsor
      </Typography>
      <Grid container spacing={2} sx={{ mb: 6 }}>
        {SPONSOR_INTEREST_CATEGORIES.map((cat) => (
          <Grid item xs={12} sm={6} md={4} key={cat.id}>
            <Card
              sx={{
                height: '100%',
                bgcolor: 'rgba(255,255,255,0.04)',
                border: '1px solid rgba(255,255,255,0.08)',
                borderRadius: 3,
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 16px 40px rgba(0,0,0,0.35)',
                  borderColor: 'rgba(99, 102, 241, 0.4)',
                },
              }}
            >
              <CardContent>
                <Box sx={{ color: '#818cf8', mb: 1.5 }}>{typeIcons[cat.id] || <CampaignIcon />}</Box>
                <Typography variant="h6" sx={{ fontWeight: 900, color: '#f1f5f9', mb: 1 }}>{cat.label}</Typography>
                <Typography variant="body2" sx={{ color: '#94a3b8' }}>
                  {cat.id === 'contest' && 'Title and tiered branding on public contest hubs, leaderboards, and submission flows.'}
                  {cat.id === 'training_video' && 'Logo and credits on stroke, drill, and fitness training segments in learner journeys.'}
                  {cat.id === 'content_series' && 'Co-branded rubric and learning modules across sport encyclopedia pages.'}
                  {cat.id === 'tournament' && 'League and tournament naming rights as our tournament module expands.'}
                  {cat.id === 'new_format' && 'First-mover branding on fun, creative, and experimental contest formats.'}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={3} sx={{ mb: 6 }}>
        <Grid item xs={12} md={4}>
          <Box sx={{ p: 2.5, borderRadius: 3, bgcolor: 'rgba(14, 165, 233, 0.12)', border: '1px solid rgba(14, 165, 233, 0.25)', height: '100%' }}>
            <TierIcon sx={{ color: '#38bdf8', fontSize: 32, mb: 1 }} />
            <Typography variant="h6" sx={{ fontWeight: 900, color: '#e0f2fe', mb: 1 }}>Multi-tier model</Typography>
            <Typography variant="body2" sx={{ color: '#94a3b8' }}>
              Title, Gold, Silver, Bronze, and Supporting tiers — matched to your budget band and slot competition in each category.
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={12} md={4}>
          <Box sx={{ p: 2.5, borderRadius: 3, bgcolor: 'rgba(245, 158, 11, 0.12)', border: '1px solid rgba(245, 158, 11, 0.25)', height: '100%' }}>
            <ContestIcon sx={{ color: '#fbbf24', fontSize: 32, mb: 1 }} />
            <Typography variant="h6" sx={{ fontWeight: 900, color: '#fef3c7', mb: 1 }}>Contests open now</Typography>
            <Typography variant="body2" sx={{ color: '#94a3b8' }}>
              Public sport contest tabs are live. Sponsor visibility travels with skills, drills, fitness, and creative format challenges.
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={12} md={4}>
          <Box sx={{ p: 2.5, borderRadius: 3, bgcolor: 'rgba(99, 102, 241, 0.12)', border: '1px solid rgba(99, 102, 241, 0.25)', height: '100%' }}>
            <GlobalIcon sx={{ color: '#a5b4fc', fontSize: 32, mb: 1 }} />
            <Typography variant="h6" sx={{ fontWeight: 900, color: '#e0e7ff', mb: 1 }}>Built for global reach</Typography>
            <Typography variant="body2" sx={{ color: '#94a3b8' }}>
              Architecture supports worldwide brands; our rollout prioritises India while welcoming international partners.
            </Typography>
          </Box>
        </Grid>
      </Grid>

      <Box id="express-interest" sx={{ scrollMarginTop: 96 }}>
        <ExpressInterestForm />
      </Box>
    </SponsorPublicShell>
  );
};

export default SponsorLandingPage;
